/**
 * Shared helpers for hierarchical serial numbers (1, 1.1, 1.1.1).
 * Tree index is built in O(n) once per items array change.
 */

const NON_NUMBERED_TYPES = new Set(["title", "guidance"]);

export const isNumberedType = (type) =>
  type != null && !NON_NUMBERED_TYPES.has(type);

export const formatSerialNumber = (prefix, index) => {
  if (index == null || index < 1) return "";
  if (!prefix) return String(index);
  return `${prefix}.${index}`;
};

export const getItemOrderIndex = (items, getId) => {
  const orderIndex = new Map();
  items.forEach((item, index) => {
    const id = getId(item);
    if (id != null && !orderIndex.has(id)) {
      orderIndex.set(id, index);
    }
  });
  return orderIndex;
};

/**
 * @param {Array} items
 * @param {(item) => string} getId
 * @param {(item) => string[]} getTriggerIds - returns parent trigger ids from displayCondition
 */
export const buildChildrenByParent = (items, getId, getTriggerIds) => {
  const childrenByParent = new Map();
  const childIds = new Set();
  const OVERRIDDEN_IDS = new Set([
    "scope3_categories_capital_goods_table",
    "scope3_categories_non_capital_goods_table",
  ]);

  items.forEach((item) => {
    let triggerIds = getTriggerIds(item) || [];
    if (OVERRIDDEN_IDS.has(getId(item))) {
      triggerIds = ["scope3_first"];
    }
    triggerIds.forEach((parentId) => {
      if (!parentId) return;
      childIds.add(getId(item));
      if (!childrenByParent.has(parentId)) {
        childrenByParent.set(parentId, []);
      }
      const siblings = childrenByParent.get(parentId);
      const itemId = getId(item);
      if (!siblings.some((s) => getId(s) === itemId)) {
        siblings.push(item);
      }
    });
  });

  return { childrenByParent, childIds };
};

export const getRootItems = (items, childIds, getId) =>
  items.filter((item) => !childIds.has(getId(item)));

const getQuestionTriggerIds = (question) =>
  (question.displayCondition || [])
    .map((c) => c.triggerQuestionId || c.triggerFieldId)
    .filter(Boolean);

const getFieldTriggerIds = (field) =>
  (field.displayCondition || [])
    .map((c) => c.triggerQuestionId || c.triggerFieldId)
    .filter(Boolean);

export const buildQuestionTree = (questions = []) => {
  const getId = (q) => q.questionId;
  const { childrenByParent, childIds } = buildChildrenByParent(
    questions,
    getId,
    getQuestionTriggerIds,
  );
  const orderIndex = getItemOrderIndex(questions, getId);
  const roots = getRootItems(questions, childIds, getId);

  return { childrenByParent, childIds, orderIndex, roots };
};

export const buildFieldTree = (fields = []) => {
  const getId = (f) => f.fieldId;
  const { childrenByParent, childIds } = buildChildrenByParent(
    fields,
    getId,
    getFieldTriggerIds,
  );
  const orderIndex = getItemOrderIndex(fields, getId);
  const roots = getRootItems(fields, childIds, getId);

  return { childrenByParent, childIds, orderIndex, roots };
};

/**
 * Sort siblings by stable source-array order.
 */
export const sortByOrderIndex = (items, orderIndex, getId) =>
  [...items].sort(
    (a, b) =>
      (orderIndex.get(getId(a)) ?? Number.MAX_SAFE_INTEGER) -
      (orderIndex.get(getId(b)) ?? Number.MAX_SAFE_INTEGER),
  );

/**
 * Assign hierarchical numbers to visible numbered siblings.
 * @returns {Map<string, string>} id -> serial number string (without trailing dot)
 */
export const assignVisibleSerialNumbers = ({
  items,
  orderIndex,
  getId,
  getType,
  isVisible,
  parentNumber = "",
}) => {
  const numbers = new Map();
  const visible = sortByOrderIndex(
    items.filter((item) => isVisible(item)),
    orderIndex,
    getId,
  );

  let siblingIndex = 0;
  visible.forEach((item) => {
    if (!isNumberedType(getType(item))) return;
    siblingIndex += 1;
    numbers.set(getId(item), formatSerialNumber(parentNumber, siblingIndex));
  });

  return numbers;
};

export const formatSerialDisplay = (number) => (number ? `${number}. ` : "");

/**
 * Walk field tree and assign serial numbers to visible numbered fields.
 * @returns {Map<string, string>}
 */
export const computeFieldNumbersForContext = ({
  fieldTree,
  parentQuestionNumber = "",
  isFieldVisible,
  getFieldType = (field) => field.fieldType,
  skipFieldIds = new Set(),
}) => {
  const numbers = new Map();

  const walk = (parentFieldId, parentNumber) => {
    let candidates;
    if (parentFieldId == null) {
      candidates = fieldTree.roots.filter(
        (field) => !skipFieldIds.has(field.fieldId),
      );
    } else {
      candidates = fieldTree.childrenByParent.get(parentFieldId) || [];
    }

    const visible = sortByOrderIndex(
      candidates.filter(isFieldVisible),
      fieldTree.orderIndex,
      (field) => field.fieldId,
    );

    let siblingIndex = 0;
    visible.forEach((field) => {
      let currentNumber = parentNumber;
      if (isNumberedType(getFieldType(field))) {
        siblingIndex += 1;
        currentNumber = formatSerialNumber(parentNumber, siblingIndex);
        numbers.set(field.fieldId, currentNumber);
      }
      walk(field.fieldId, currentNumber);
    });
  };

  walk(null, parentQuestionNumber || "");
  return numbers;
};
